import React from 'react';
import { useAnalysisType } from '../../contexts/AnalysisTypeContext';

interface AnalysisTypeToggleProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export const AnalysisTypeToggle: React.FC<AnalysisTypeToggleProps> = ({
  className = '',
  size = 'md',
  showLabel = true
}) => {
  const {
    currentAnalysisType,
    availableAnalysisTypes,
    canSwitchAnalysisType,
    setAnalysisType,
    isLoading
  } = useAnalysisType();

  // Don't render if user can't switch or only has one option
  if (!canSwitchAnalysisType || availableAnalysisTypes.length <= 1) {
    return null;
  }

  const buttonSizeClass = size === 'sm'
    ? 'px-2 py-0.5 text-xs'
    : size === 'lg'
      ? 'px-4 py-1.5 text-sm'
      : 'px-3 py-1 text-sm';

  const currentType = availableAnalysisTypes.find(t => t.id === currentAnalysisType);

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      {showLabel && (
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Analysis Type:
        </span>
      )}

      <div className="flex rounded-full bg-gray-100 dark:bg-gray-700 p-0.5 gap-0.5">
        {availableAnalysisTypes.map((type) => (
          <button
            key={type.id}
            type="button"
            onClick={() => { if (!isLoading) setAnalysisType(type.id); }}
            disabled={isLoading}
            aria-pressed={currentAnalysisType === type.id}
            aria-label={`Switch to ${type.title} analysis`}
            title={type.title}
            className={`
              ${buttonSizeClass} rounded-full transition-all duration-150 whitespace-nowrap
              ${currentAnalysisType === type.id
                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 font-semibold shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }
              ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            {type.name}
          </button>
        ))}
      </div>

      {showLabel && currentType?.description && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {currentType.description}
        </div>
      )}
    </div>
  );
};

// Compact version for headers
export const AnalysisTypeToggleCompact: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <AnalysisTypeToggle
      className={className}
      size="sm"
      showLabel={false}
    />
  );
};

// Full version with description
export const AnalysisTypeToggleFull: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <AnalysisTypeToggle
      className={className}
      size="md"
      showLabel={true}
    />
  );
};
